import { paymentEntryParser } from '@oresundsbron/validators';
import { pipe } from 'fp-ts/lib/function';
import {
  chain,
  fromPredicate,
  fromTask,
  getOrElseW,
  map,
} from 'fp-ts/lib/TaskOption';
import { of } from 'fp-ts/lib/Task';
import { Trans, useTranslation } from 'next-i18next';
import { FC, useCallback, useEffect } from 'react';
import { useCountriesOptGroups } from '../../../hooks/useCountries';
import { useEmissionClasses } from '../../../hooks/useEmissionClasses';
import { useValidateAlpr } from '../../../hooks/useValidateAlpr';
import { validateT } from '../../../lib/zod';
import { pick } from '../../../lib/data';
import { chainValidations, isFieldErrorEmpty } from '../../../lib/errors';
import {
  Alpr,
  Data,
  BusinessRegisterAlpr,
} from '../../../components/Forms/RegisterAlpr/Bussiness';
import { Data as TermsData } from '../../../components/Forms/TermsOfAgreement/Business';
import { Typography } from '@oresundsbron/bridge-ui';
import { findFirst } from 'fp-ts/lib/Array';
import { match } from 'fp-ts/lib/Option';
import {
  useForm,
  useSubscribe,
  ValidationResult,
} from '@oresundsbron/use-form';
import {
  discountMainContract,
  removeDiscountMainContract,
  useContractsStore,
} from '../../../stores/contracts';
import { Lens } from 'monocle-ts';
import { SignUpFormPart } from '../utils';
import { veColorList } from '~/utils';
import { useCountriesBvSupport } from '@oresundsbron/app-common';

const pickLicensePlateAndCountry = pick<Alpr>()('licensePlate', 'country');

const paymentEntries = Lens.fromNullableProp<Data>()('paymentEntries', []);

const findHGV = (data: Alpr[]) =>
  pipe(
    data,
    findFirst((x) => x.weightClass === 'true')
  );

export const Payment: FC<SignUpFormPart & { commuter?: boolean }> = ({
  namespace,
  titleTranslationKey,
  commuter,
  ns,
}) => {
  const bvSupportCountries = useCountriesBvSupport();
  const countries = useCountriesOptGroups();
  const emissionClasses = useEmissionClasses();
  const validateAlpr = useValidateAlpr();

  const { t } = useTranslation(ns, {
    nsMode: 'fallback',
  });

  const { getData } = useForm<Data>({
    namespace,
    title: t(titleTranslationKey),
  });

  const pickAndValidateAlpr = useCallback(
    (data: Alpr) => (errors?: ValidationResult<Alpr>) =>
      pipe(
        errors,
        fromPredicate(isFieldErrorEmpty('licensePlate')),
        map(() => data),
        map(pickLicensePlateAndCountry),
        chain(fromPredicate((x) => !!x.country && !!x.licensePlate)),
        chain(
          validateAlpr<Alpr>({
            licensePlate: t('form.validations.licensePlate.exist'),
          })
        )
      ),
    [t, validateAlpr]
  );

  const pickAndCheckLocalEntries = useCallback(
    (data: Alpr) => () =>
      pipe(
        getData(),
        paymentEntries.get,
        fromPredicate((x) =>
          x.some((e) => e.licensePlate === data.licensePlate)
        ),
        map(() => ({ licensePlate: t('form.validations.licensePlate.unique') }))
      ),
    [getData, t]
  );

  const validateLicensePlate = useCallback(
    (data: Alpr) =>
      pipe(
        data,
        validateT(paymentEntryParser.spa, t),
        fromTask,
        chainValidations(
          pickAndCheckLocalEntries(data),
          pickAndValidateAlpr(data)
        ),
        getOrElseW(() => of(undefined))
      )(),
    [pickAndValidateAlpr, t, pickAndCheckLocalEntries]
  );
  const [currency] = useSubscribe<TermsData>('termsOfAgreement', 'currency');
  const { main } = useContractsStore();
  useEffect(
    () =>
      pipe(
        getData(),
        paymentEntries.get,
        findHGV,
        match(
          () => {
            removeDiscountMainContract();
          },
          () => {
            discountMainContract({
              currency: currency as string,
              price: 0,
              contractType: main.value,
              productCode: '',
              priceInclVAT: 0,
            });
          }
        )
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getData, currency]
  );

  const vehicleColors = veColorList.map((value) => ({
    label: t(`vehicleColors.${value}`),
    value,
  }));

  const regAttr = {
    namespace,
    title: t(titleTranslationKey),
    description: (
      <Trans i18nKey="form.sections.payment.description" ns={ns}>
        <Typography className="mb-4" />
        <Typography size="sm" />
      </Trans>
    ),
    maxEntriesDisclaimer: (
      <Trans
        i18nKey="form.sections.payment.disclaimers.numberOfEntries"
        ns={ns}
      >
        <Typography as="span" weight="medium" />
      </Trans>
    ),
    skipAlprDisclaimer: t('form.sections.payment.disclaimers.skipAlpr'),
    onAlprValidation: validateLicensePlate,
    countryOptions: countries,
    bvSupportCountries: bvSupportCountries,
    maxEntries: commuter ? 1 : 2,
    commuter,
    labels: {
      skipAlpr: t('form.labels.skipAlpr'),
      licensePlate: t('form.labels.licensePlate'),
      country: t('form.labels.country'),
      fuelType: t('form.labels.fuelType'),
      emissionClass: t('form.labels.emissionClass'),
      weightClass: t('form.labels.weightClass'),
      useInNorway: t('form.labels.useInNorway'),
      add: t('form.labels.add'),
      hybrid: t('form.labels.hybrid'),
      addMore: t('form.labels.addMore'),
      cancel: t('action.cancel'),
      remove: t('action.remove'),
      loading: t('action.loading'),
      orderBizz: t('form.labels.orderBizz'),
      bizzOrdered: t('form.sections.payment.disclaimers.bizzOrdered'),
      bizz: t('form.labels.bizz'),
      vehicleSearch: t('action.search'),
      make: t('form.labels.make'),
      model: t('form.labels.model'),
      color: t('form.labels.color'),
      alprName: t('form.labels.alprName'),
      volVehicleInfo: t('form.labels.vlinfo'),
      noVehicleInfo: t('form.validations.licensePlate.notfound'),
      licensePlateExist: t('form.validations.licensePlate.exist'),
      loadingSearch: t('action.loading'),
      // Not used
      isSBF: '',
    },
    placeholders: {
      weightClass: t('form.placeholders.weightClass'),
      country: t('form.placeholders.country'),
      emissionClass: t('form.placeholders.emissionClass'),
      fuelType: t('form.placeholders.fuelType'),
      color: t('form.placeholders.color'),
    },
    useOnOtherPlacesDisclaimer: t(
      'form.sections.payment.disclaimers.useInNorway'
    ),
    fuelTypeOptions: emissionClasses.fuelTypes,
    weightClassOptions: [
      { label: t('weightClasses.light'), value: 'false' },
      { label: t('weightClasses.heavy'), value: 'true' },
    ],
    emissionClassOptions: emissionClasses.emissionClasses,
    vehicleColors,
  };

  return <BusinessRegisterAlpr {...regAttr} />;
};
